import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { authenticatedFetch } from '../services/authService';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first for immediate UI
    try {
      const stored = localStorage.getItem('theme');
      if (stored === 'dark' || stored === 'light') return stored as Theme;
    } catch {}
    // Default to light theme (don't use system preference to avoid confusion)
    return 'light';
  });

  // Load theme from preferences
  const loadTheme = async () => {
    try {
      const api = await authenticatedFetch('/api/preferences?namespace=ui.theme', { credentials: 'include', cache: 'no-store' });
      if (api.ok) {
        const pref = await api.json();
        if (pref && pref.success && pref.value && typeof pref.value === 'object' && pref.value.mode) {
          const themeValue = pref.value.mode as string;
          if (themeValue === 'dark' || themeValue === 'light') {
            setThemeState(themeValue as Theme);
            applyTheme(themeValue as Theme);
            return;
          }
        }
      }
    } catch {}
  };

  // Apply theme to document
  const applyTheme = (newTheme: Theme) => {
    try {
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      localStorage.setItem('theme', newTheme);
    } catch {}
  };

  // Save theme to preferences
  const saveTheme = async (newTheme: Theme) => {
    try {
      await authenticatedFetch('/api/preferences', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          namespace: 'ui.theme',
          scope: 'user',
          key: 'mode',
          value: { mode: newTheme }
        })
      });
    } catch {}
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
    if (isAuthenticated) {
      saveTheme(newTheme);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  // Load theme on mount and when authenticated
  useEffect(() => {
    applyTheme(theme);
    if (isAuthenticated) {
      loadTheme();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      loadTheme();
    }
  }, [isAuthenticated, user?.id]);

  // Listen to system preference changes
  useEffect(() => {
    try {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        // Only auto-switch if no user preference is set
        if (isAuthenticated) {
          // Check if user has a saved preference
          authenticatedFetch('/api/preferences?namespace=ui.theme', { credentials: 'include' })
            .then(res => res.ok ? res.json() : null)
            .then(pref => {
              if (!pref || !pref.success || !pref.value || !pref.value.mode) {
                // No user preference, use system
                setTheme(e.matches ? 'dark' : 'light');
              }
            })
            .catch(() => {});
        } else {
          setTheme(e.matches ? 'dark' : 'light');
        }
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } catch {}
  }, [isAuthenticated]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

